import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ModalCard from '../../components/ui/ModalCard';
import SocialAuthButtons from '../../components/authentication/SocialAuthButtons';
import TextInput from '../../components/ui/TextInput';
import { EyeIcon, EyeOffIcon } from '../../components/icons';
import { login as loginApi } from '../../api/authApi';
import { getMe } from '../../api/userApi';
import { storeToken } from '../../api/httpClient';
import { useAuth } from '../../context/AuthContext';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = email.trim() !== '' && password !== '' && termsAccepted && !submitting;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { jwtToken } = await loginApi(email, password);
      storeToken(jwtToken);
      const user = await getMe();
      auth.login(jwtToken, user);
      navigate('/parent/home');
    } catch {
      setError(t('login.badCredentials'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSocialLogin = (provider: 'google' | 'apple') => {
    // TODO(backend): kick off the OAuth flow for the given provider.
    // eslint-disable-next-line no-console
    console.info('[mock] social login', provider);
  };

  return (
    <ModalCard
      onSubmit={handleSubmit}
      footer={
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          <Box component="span" sx={(theme) => ({ color: theme.app.foreground })}>
            {t('login.noAccount')}
          </Box>
          <Link
            component={RouterLink}
            to="/register"
            underline="hover"
            sx={(theme) => ({ fontWeight: 500, color: theme.app.link })}
          >
            {t('login.createAccount')}
          </Link>
        </Typography>
      }
    >
      <Typography variant="h2" component="h1">
        {t('login.headline')}
        <Box component="span" sx={(theme) => ({ color: theme.app.accent })}>
          {t('login.headlineAction')}
        </Box>
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextInput
          id="login-email"
          type="email"
          autoComplete="email"
          label={t('login.emailLabel')}
          placeholder={t('login.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextInput
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            label={t('login.passwordLabel')}
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            endAdornment={
              <IconButton
                size="small"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={t(showPassword ? 'login.hidePassword' : 'login.showPassword')}
                sx={{ color: 'text.secondary' }}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </IconButton>
            }
          />
          <Link
            component={RouterLink}
            to="/forgot-password"
            underline="hover"
            variant="body2"
            sx={(theme) => ({ fontWeight: 500, color: theme.app.link })}
          >
            {t('login.forgotPassword')}
          </Link>
        </Box>
      </Box>

      <FormControlLabel
        sx={{ alignItems: 'flex-start', m: 0, gap: 1.5 }}
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            sx={{ p: 0, color: 'text.secondary' }}
          />
        }
        label={
          <Typography variant="body2" sx={(theme) => ({ color: theme.app.foreground })}>
            <Trans
              i18nKey="login.terms"
              components={{
                terms: <Link href="#" underline="always" color="inherit" />,
                privacy: <Link href="#" underline="always" color="inherit" />,
              }}
            />
          </Typography>
        }
      />

      {error && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!canSubmit}
        sx={(theme) => ({
          backgroundColor: theme.app.primary,
          color: theme.app.primaryForeground,
          '&:hover': { backgroundColor: theme.app.primaryHover },
        })}
      >
        {t('login.submit')}
      </Button>

      <SocialAuthButtons onSelect={handleSocialLogin} />
    </ModalCard>
  );
}
